import { useState, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import apiClient from '../../../lib/apiClient';

/**
 * Multi-vendor coverage overlay.
 *
 * Maintains a set of selected vendor IDs and fetches `GET /vendors/:id/overlay`
 * for each via `useQueries` so React Query caches them independently. Caller
 * receives the full list of overlays and helpers to add/remove/clear vendors.
 *
 * The return shape preserves `selectedVendorId` / `overlay` / `selectVendor`
 * for the single-vendor legacy callers (ZeroTrust.js). When more than one
 * vendor is selected, those three fall back to the first-selected vendor.
 */
export function useVendorOverlay() {
  // Ordered so the picker can render dots in a stable, predictable sequence.
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);

  const queries = useQueries({
    queries: selectedVendorIds.map(id => ({
      queryKey: ['zt-vendor-overlay', id],
      queryFn: () => apiClient.get(`/api/zero-trust/vendors/${id}/overlay`),
      staleTime: 5 * 60_000,
    })),
  });

  const overlays = queries
    .map(q => q.data || null)
    .filter(Boolean);

  const loading = queries.some(q => q.isLoading);
  const error = queries.find(q => q.error)?.error || null;

  const toggleVendor = useCallback((vendorId) => {
    if (vendorId == null) return;
    setSelectedVendorIds(prev => (
      prev.includes(vendorId)
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    ));
  }, []);

  const clearVendors = useCallback(() => {
    setSelectedVendorIds([]);
  }, []);

  // --- Legacy single-vendor shim for callers that haven't migrated yet ---
  const selectVendor = useCallback((vendorId) => {
    if (vendorId == null) {
      setSelectedVendorIds([]);
    } else {
      setSelectedVendorIds([vendorId]);
    }
  }, []);

  return {
    selectedVendorIds,
    overlays,
    loading,
    error,
    toggleVendor,
    clearVendors,

    // Back-compat (single-vendor).
    selectedVendorId: selectedVendorIds[0] ?? null,
    overlay: overlays[0] || null,
    selectVendor,
  };
}
